/**
 * `sanitizeHtml` — shared DOMPurify wrapper for the small inline HTML surfaces
 * (email signature previews, template snippets) that render org/user-supplied
 * HTML straight into the DOM rather than the sandboxed iframe.
 *
 * `EmailHtmlRenderer` already isolates full email bodies in a null-origin
 * iframe; this helper covers the lightweight previews where an iframe would be
 * disproportionate but the stored-XSS risk is identical (a teammate's signature
 * renders into every operator's UI). One sanitiser, reused everywhere — callers
 * never import `dompurify` directly.
 */
import DOMPurify from "dompurify";

/**
 * Link hardening — registered once per module load.
 *
 * DOMPurify keeps `<a href target rel>` by default, but it does NOT guarantee a
 * safe `rel` on links that open in a new tab. A signature (or a Genera-con-Ando
 * HTML draft) can carry `<a target="_blank">` with no `rel`, which is a
 * reverse-tabnabbing vector. This hook re-asserts
 * `rel="noopener noreferrer"` on every anchor that opens a new tab, and keeps
 * the `nofollow` our own `linkifyHtml` already sets. Anchors are the ONLY tag we
 * touch — everything else flows through the default DOMPurify policy unchanged.
 */
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName !== "A") return;
  const el = node as unknown as Element;
  if (!el.hasAttribute("href")) return;
  if (el.getAttribute("target") === "_blank") {
    const rel = (el.getAttribute("rel") || "").toLowerCase();
    const tokens = new Set(rel.split(/\s+/).filter(Boolean));
    tokens.add("noopener");
    tokens.add("noreferrer");
    el.setAttribute("rel", Array.from(tokens).join(" "));
  }
});

/**
 * Sanitise an HTML fragment for safe `dangerouslySetInnerHTML` rendering.
 * Strips `<script>`, event-handler attributes and dangerous URIs while keeping
 * the formatting tags a signature / template snippet legitimately uses. Anchors
 * that open a new tab are hardened with `rel="noopener noreferrer"` (see hook
 * above) so bare `target="_blank"` links can never leak `window.opener`.
 */
export function sanitizeHtml(rawHtml: string): string {
  return DOMPurify.sanitize(rawHtml) as unknown as string;
}
